import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';
import {
  getScrollContainer,
  getScrollTop,
  getScrollEventTarget,
  getIntersectionRoot,
  scrollContainerMediaQuery,
} from '@theme/scroll-container';

/**
 * AH: the always-on bar (ah-theme-ops/header-bar-prd_draft.md, v2). Only scrolling a shopper clearly means changes it:
 * a run of 32px down slims it (desktop) or slides it away (phones); a run of 64px up brings it back. Pixels, ms.
 */
const AH_BAR = { reversal: 12, gestureGap: 300, down: 32, up: 64, settle: 500 };
const AH_INPUT_EVENTS = [
  'wheel',
  'keydown',
  'touchstart',
  'touchend',
  'touchcancel',
  'pointerdown',
  'pointerup',
  'pointercancel',
  'pointerover',
  'pointerout',
];
const ahNarrowQuery = window.matchMedia('(max-width: 749px)');

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /** AH: always-on bar state, see AH_BAR. @type {'full' | 'condensed'} */
  #ahBar = 'full';
  #ahRunDir = 0;
  #ahRun = 0;
  #ahAgainst = 0;
  #ahLastFrameAt = 0;
  #ahSettleUntil = 0;
  #ahInputUntil = 0;
  #ahHeld = false;
  #ahOverFrame = false;
  #ahFocusIntent = false;
  #ahLastEscAt = -Infinity;
  /** @type {number | null} */
  #ahLastY = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }

    // AH: a layout change at the top of the page always shows the full bar; keep the visible height and line current.
    if (this.getAttribute('sticky') === 'always') {
      if (getScrollTop() <= this.offsetHeight) this.#ahSetBar('full');
      this.#ahSetVisibleHeight();
      this.#ahSetLineRatio();
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
      root: getIntersectionRoot(),
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
        // AH: idle (invisible) is wrong once the header is back at its natural spot, for example after a rotation
        // across the 990px breakpoint rebuilt this observer at the top of the page. Show it. The live position is
        // checked, same rule as #updateScrollState, because the entry can be stale when the main thread is busy.
        if (!this.#offscreen && this.dataset.stickyState === 'idle' && this.getBoundingClientRect().top >= 0) {
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        }
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
      // The drawer squeeze can trigger minimum-reached at desktop widths where
      // it normally wouldn't. Once the menu hides, the overflow-list is
      // display:none and can't measure to clear it. Resetting it here so
      // setHeaderMenuStyle() sees a clean state.
      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  /**
   * Rebinds the scroll listener and IntersectionObserver when the viewport
   * crosses the squeeze breakpoint (990px). The scroll container switches
   * between `.page-wrapper` (desktop) and `document.scrollingElement` (mobile),
   * so cached bindings from initialization become stale after a resize.
   */
  #handleBreakpointChange = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    // Rebind scroll listener
    if (this.#scrollContainer) {
      this.#scrollContainer.removeEventListener('scroll', this.#handleWindowScroll);
      this.#scrollContainer = getScrollEventTarget();
      this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
    }

    // Recreate IntersectionObserver with the new root
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#observeStickyPosition(stickyMode === 'always');

    // AH: the new scroll container starts at the top with no scroll event, so show the full bar there.
    if (stickyMode === 'always') this.#ahReset();
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    // AH: keep the last position current while the header is on screen. Stock returned without it, so a jump off
    // screen followed by a jump back to the top read as "not scrolling up" and left the header invisible at the top.
    if (!this.#offscreen && stickyMode !== 'always') {
      this.#lastScrollTop = getScrollTop();
      return;
    }

    const scrollTop = getScrollTop();
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop = headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#ahUpdateBar(scrollTop);
      this.#lastScrollTop = scrollTop;
      return;
    }

    // AH: back at its natural spot the header resets whichever way the page moved; stock only reset on the way up.
    if (isAtTop) {
      // reset sticky state when header is scrolled up to natural position
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
    } else if (isScrollingUp) {
      // show sticky header when scrolling up
      this.dataset.stickyState = 'active';
      this.dataset.scrollDirection = 'up';
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  // ---- AH: always-on bar (header-bar-prd_draft.md v2, fixes from the verify round wf_beb0c677-986) ----

  #ahResetRun() {
    this.#ahRunDir = 0;
    this.#ahRun = 0;
    this.#ahAgainst = 0;
  }

  /** Back to full, after the page itself jumped (a new scroll container, a layout change). No slide: the page moved, not the shopper. */
  #ahReset() {
    this.#ahResetRun();
    if (this.#ahBar !== 'full') {
      delete this.dataset.ahBarReady;
      this.#ahSetBar('full');
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (this.isConnected) this.dataset.ahBarReady = '';
        })
      );
    }
    this.#ahSetVisibleHeight();
    this.#ahSetLineRatio();
    this.#ahLastY = getScrollTop();
  }

  /** @param {'full' | 'condensed'} state */
  #ahSetBar(state) {
    if (this.#ahBar === state) return;
    this.#ahBar = state;
    this.#ahResetRun();
    this.dataset.ahBar = state;
    this.#ahSetVisibleHeight();
  }

  #ahIsDrawerLayout() {
    return this.dataset.menuStyle === 'drawer' || ahNarrowQuery.matches;
  }

  /** The bar height the shopper can see, for the cart summary's sticky offset. Never refs.headerRowTop: that ref is on both rows. */
  #ahSetVisibleHeight() {
    if (this.getAttribute('sticky') !== 'always') return;
    let visible = this.offsetHeight;
    if (this.#ahBar === 'condensed') {
      const topRow = /** @type {HTMLElement | null} */ (this.querySelector('.header__row--top'));
      visible = this.#ahIsDrawerLayout() ? 0 : topRow?.offsetHeight ?? visible;
    }
    document.body.style.setProperty('--ah-header-visible', `${visible}px`);
  }

  /** B1: the divider's width as a share of the bar. The slim bar stretches it to full width and dims it by this ratio. */
  #ahSetLineRatio() {
    const row = this.querySelector('.header__row--top');
    const columns = row?.querySelector('.header__columns');
    if (!row || !columns) return;
    const ratio = columns.getBoundingClientRect().width / (row.getBoundingClientRect().width || 1);
    this.style.setProperty('--ah-line-ratio', Math.min(1, ratio).toFixed(4));
  }

  /** Hold the bar while a dialog, drawer or menu is open, or a keyboard user is in the header. */
  #ahShouldFreeze() {
    if (document.documentElement.hasAttribute('scroll-lock') || document.visibilityState === 'hidden') return true;
    if (this.#ahFocusIntent || document.querySelector('theme-drawer[open]')) return true;
    if (this.querySelector("details[open], [slot='more'] [aria-expanded='true']")) return true;
    return Boolean(this.querySelector('shopify-account')?.shadowRoot?.querySelector('dialog[open]'));
  }

  /** @param {number} rawTop */
  #ahUpdateBar(rawTop) {
    const container = getScrollContainer();
    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const y = Math.min(Math.max(rawTop, 0), maxTop);
    const now = performance.now();

    if (y <= this.offsetHeight) {
      this.#ahResetRun();
      this.#ahLastY = y;
      this.#ahSetBar('full');
      return;
    }

    if (this.#ahLastY === null || now < this.#ahSettleUntil || this.#ahShouldFreeze()) {
      this.#ahResetRun();
      this.#ahLastY = y;
      return;
    }

    const prev = Math.min(this.#ahLastY, maxTop);
    const rawDy = y - prev;
    // The run starts at the top zone's edge, not at the last frame that happened to land inside it.
    const dy = y - Math.max(prev, this.offsetHeight);
    this.#ahLastY = y;

    // Only scrolling the shopper is doing counts; image loads, restores and script scrolls just re-baseline.
    const fromInput = this.#ahHeld || this.#ahOverFrame || now < this.#ahInputUntil;
    if (!fromInput || Math.abs(rawDy) >= Math.max(400, 0.9 * container.clientHeight)) {
      this.#ahResetRun();
      return;
    }

    if (now - this.#ahLastFrameAt > AH_BAR.gestureGap) this.#ahResetRun();
    this.#ahLastFrameAt = now;
    if (dy === 0) return;

    const dir = Math.sign(dy);
    if (this.#ahRunDir === 0 || dir === this.#ahRunDir) {
      this.#ahRunDir = dir;
      this.#ahRun += Math.abs(dy);
      this.#ahAgainst = 0;
    } else {
      this.#ahAgainst += Math.abs(dy);
      // Net travel: a wobble back and forth never adds up to a run.
      this.#ahRun = Math.max(0, this.#ahRun - Math.abs(dy));
      if (this.#ahAgainst < AH_BAR.reversal) return;
      this.#ahRunDir = dir;
      this.#ahRun = this.#ahAgainst;
      this.#ahAgainst = 0;
    }

    if (this.#ahRunDir > 0 && this.#ahRun >= AH_BAR.down && this.#ahBar !== 'condensed') this.#ahSetBar('condensed');
    else if (this.#ahRunDir < 0 && this.#ahRun >= AH_BAR.up && this.#ahBar !== 'full') this.#ahSetBar('full');
  }

  /** @param {Event} event */
  #ahOnInput = (event) => {
    const now = performance.now();
    const until = (/** @type {number} */ ms) => {
      this.#ahInputUntil = Math.max(this.#ahInputUntil, now + ms);
    };
    switch (event.type) {
      case 'wheel':
        this.#ahFocusIntent = false;
        until(400);
        break;
      case 'keydown':
        if (/** @type {KeyboardEvent} */ (event).key === 'Escape') this.#ahLastEscAt = now;
        until(1200);
        break;
      case 'touchstart':
        this.#ahFocusIntent = false;
        this.#ahHeld = true;
        break;
      case 'touchend':
      case 'touchcancel':
        // Not `instanceof TouchEvent`: desktop Firefox has no TouchEvent global.
        this.#ahHeld = 'touches' in event && /** @type {TouchEvent} */ (event).touches.length > 0;
        until(3000);
        break;
      case 'pointerover':
      case 'pointerout':
        // Wheel and touch inside an iframe (a video, Shop Pay) never reach this window.
        this.#ahOverFrame = event.type === 'pointerover' && event.target instanceof HTMLIFrameElement;
        break;
      default:
        // Pointer down/up: mouse and pen only. Touch is handled above, since the browser cancels a pointer when it takes a swipe.
        if (!(event instanceof PointerEvent) || event.pointerType === 'touch') break;
        this.#ahHeld = event.type === 'pointerdown';
        if (!this.#ahHeld) until(1200);
    }
  };

  /** A keyboard user in the header gets the full bar and keeps it until focus leaves. Not focus a closing dialog hands back. */
  #ahOnFocusIn = (/** @type {FocusEvent} */ event) => {
    if (performance.now() - this.#ahLastEscAt < 600) return;
    const target = event.composedPath()[0];
    if (target instanceof Element && target.matches(':focus-visible')) {
      this.#ahFocusIntent = true;
      this.#ahSetBar('full');
    }
  };

  #ahOnFocusOut = (/** @type {FocusEvent} */ event) => {
    if (!(event.relatedTarget instanceof Node) || !this.contains(event.relatedTarget)) this.#ahFocusIntent = false;
  };

  #ahObserver = new MutationObserver((records) => {
    // Horizon rewrites data-menu-style with the same value on every resize; only real changes count.
    const changed = records.filter(
      (record) => record.oldValue !== /** @type {Element} */ (record.target).getAttribute(record.attributeName ?? '')
    );
    if (!changed.length) return;
    if (changed.some((record) => record.target === this && record.attributeName === 'data-menu-style')) {
      this.#ahReset();
      return;
    }
    // A dialog, drawer or menu opened or closed: whatever scroll it restores is not a gesture.
    this.#ahSettleUntil = performance.now() + AH_BAR.settle;
    this.#ahLastY = getScrollTop();
    this.#ahResetRun();
  });

  #ahConnect() {
    this.#ahBar = 'full';
    this.dataset.ahBar = 'full';
    this.#ahLastY = getScrollTop();
    this.#ahSetVisibleHeight();
    this.#ahSetLineRatio();
    for (const type of AH_INPUT_EVENTS) window.addEventListener(type, this.#ahOnInput, { passive: true, capture: true });
    this.addEventListener('focusin', this.#ahOnFocusIn);
    this.addEventListener('focusout', this.#ahOnFocusOut);
    this.#ahObserver.observe(document.documentElement, {
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['scroll-lock'],
    });
    this.#ahObserver.observe(this, {
      attributes: true,
      attributeOldValue: true,
      subtree: true,
      attributeFilter: ['open', 'data-menu-style'],
    });
    requestAnimationFrame(() => {
      if (this.isConnected) this.dataset.ahBarReady = '';
    });
  }

  #ahDisconnect() {
    for (const type of AH_INPUT_EVENTS) window.removeEventListener(type, this.#ahOnInput, { capture: true });
    this.removeEventListener('focusin', this.#ahOnFocusIn);
    this.removeEventListener('focusout', this.#ahOnFocusOut);
    this.#ahObserver.disconnect();
    document.body.style.removeProperty('--ah-header-visible');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }

      if (stickyMode === 'always') this.#ahConnect();

      scrollContainerMediaQuery.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    scrollContainerMediaQuery.removeEventListener('change', this.#handleBreakpointChange);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#ahDisconnect();
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

// Anchor the shopify-account dialog to the bottom of the account button itself.
document.addEventListener(
  'open',
  (event) => {
    const { target } = event;
    if (!(target instanceof HTMLElement) || !target.matches('shopify-account')) return;

    const bottom = Math.max(0, Math.round(target.getBoundingClientRect().bottom));
    target.style.setProperty('--account-dialog-top', `${bottom}px`);
  },
  true
);

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
